// Course Section Variants Configuration
// Recruiters, Eligibility, Specialisation, Career Prospects, Success Stories, What Makes Course Different
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import { createBrand, updateBrand } from "@/api/admin/brand";
import {
	createCareerProspects,
	updateCareerProspects,
} from "@/api/admin/careerProspects";
import { createCaseStudy, updateCaseStudy } from "@/api/admin/caseStudy";
import {
	createCertification,
	updateCertification,
} from "@/api/admin/certification";
import { createComparison, updateComparison } from "@/api/admin/comparison";
import { getActiveCourses } from "@/api/admin/course";
import {
	createCourseOutcome,
	updateCourseOutcome,
} from "@/api/admin/courseOutcome";
import {
	cloneEligibilityWithCategories,
	createEligibility,
	createEligibilityCategory,
	deleteEligibilityCategory,
	getEligibilityByComponentId,
	getEligibilityCategoriesByComponentId,
	updateEligibility,
	updateEligibilityCategory,
} from "@/api/admin/eligibility";
import { createInstitutes, updateInstitutes } from "@/api/admin/institutes";
import { createLeaders, updateLeaders } from "@/api/admin/leaders";
import {
	createProgramBenefits,
	updateProgramBenefits,
} from "@/api/admin/programBenefits";
import { createRecruiters, updateRecruiters } from "@/api/admin/recruiters";
import { createSkills, updateSkills } from "@/api/admin/skills";
import {
	createSpecialisation,
	updateSpecialisation,
} from "@/api/admin/specialisation";
import {
	createSpecialisationCarousel,
	updateSpecialisationCarousel,
} from "@/api/admin/specialisationCarousel";
import {
	createSuccessStories,
	updateSuccessStories,
} from "@/api/admin/successStories";
import {
	createWhatMakesCourseDifferent,
	updateWhatMakesCourseDifferent,
} from "@/api/admin/whatMakesCourseDifferent";
import {
	createWhoIsThisProgramFor,
	updateWhoIsThisProgramFor,
} from "@/api/admin/whoIsThisProgramFor";
import EligibilityCriteria from "@/components/molecules/EligibilityCriteria";
import { Form_1, Form_3 } from "@/components/organisms/admin/forms";
import type { PageState } from "@/store/slices/page";
import {
	setApplicationProcess1,
	setApplicationProcess1030,
	setApplicationProcess2,
	setBrand1,
	setCareerProspects1,
	setCareerProspects2,
	setCareerProspects3,
	setCareerProspects4,
	setCaseStudy1,
	setCertification1,
	setComparison1,
	setComparison1004,
	setCourseOutcome1,
	setEligibility1,
	setEligibility1041,
	setEligibility2,
	setInstitutes1,
	setLeaders1,
	setLeaders1032,
	setProgramBenefits1,
	setProgramBenefits1005,
	setProgramBenefits1029,
	setProgramBenefits1040,
	setProgramBenefits1044,
	setProgramBenefits2,
	setProgramBenefits12,
	setProgramBenefits3,
	setProgramBenefits4,
	setProgramBenefits6,
	setProgramBenefits7,
	setRecruiters1,
	setSkills1,
	setSpecialisation1,
	setSpecialisation5017,
	setSpecialisationCarousel3000,
	setSuccessStories1,
	setSuccessStories2,
	setWhatMakesCourseDifferent1,
	setWhatMakesCourseDifferent2,
	setProgramBenefits5008,
	setProgramBenefits5009,
	setProgramBenefits1063,
	setProgramBenefits1069,
	setRecruiters5010,
	setApplicationProcess5011,
	setEligibility5012,
	setWhoIsThisProgramFor1
} from "@/store/slices/page";
import CareerProspects1 from "@/widgets/careerprospects/CareerProspects1";
import CareerProspects2 from "@/widgets/careerprospects/CareerProspects2";
import CareerProspects3 from "@/widgets/careerprospects/CareerProspects3";
import CareerProspects4 from "@/widgets/careerprospects/CareerProspects4";
import { Certificates1 } from "@/widgets/certificates/Certificates1";
import { Eligibility2 } from "@/widgets/eligibility/Eligibility2";
import Recruiters1 from "@/widgets/recruiters/Recruiters1";
import Specialisation1 from "@/widgets/specialisation/Specialisation1";
import Specialisation5017 from "@/widgets/specialisation/Specialisation5017";
import SpecialisationCarousel3000 from "@/widgets/specialisationCarousel/SpecialisationCarousel3000";
import StudentShowcase from "@/widgets/successstories/StudentShowcase";
import SuccessStories1 from "@/widgets/successstories/SuccessStories1";
import WhatMakesCourseDifferent1 from "@/widgets/whatmakescoursedifferent/WhatMakesCourseDifferent1";
import WhoIsThisProgramFor1 from "@/widgets/whoIsThisProgramFor/WhoIsThisProgramFor1";
import { APPLICATION_PROCESS_1_CONSTANTS } from "../fields/applicationProcess/applicationProcess_1";
import { APPLICATION_PROCESS_2_CONSTANTS } from "../fields/applicationProcess/applicationProcess_2";
import { BRAND_1_CONSTANTS } from "../fields/brand/brand_1";
import { CAREER_PROSPECTS_1_CONSTANTS } from "../fields/careerProspects/careerProspects_1";
import { CAREER_PROSPECTS_2_CONSTANTS } from "../fields/careerProspects/careerProspects_2";
import { CAREER_PROSPECTS_3_CONSTANTS } from "../fields/careerProspects/careerProspects_3";
import { CAREER_PROSPECTS_4_CONSTANTS } from "../fields/careerProspects/careerProspects_4";
import { CASE_STUDY_1_CONSTANTS } from "../fields/caseStudy/caseStudy_1";
import { CERTIFICATION_1_CONSTANTS } from "../fields/certification/certification_1";
import { COMPARISON_1_CONSTANTS } from "../fields/comparison/comparison_1";
import { COMPARISON_1004_CONSTANTS } from "../fields/comparison/comparison_1004";
import { COURSE_OUTCOME_1_CONSTANTS } from "../fields/courseOutcome/courseOutcome_1";
import { COURSE_OUTCOME_2_CONSTANTS } from "../fields/courseOutcome/courseOutcome_2";
import { ELIGIBILITY_1_CONSTANTS } from "../fields/eligibility/eligibility_1";
import { ELIGIBILITY_2_CONSTANTS } from "../fields/eligibility/eligibility_2";
import { INSTITUTES_1_CONSTANTS } from "../fields/institutes/institutes_1";
import { LEADERS_1_CONSTANTS } from "../fields/leaders/leaders_1";
import { PROGRAM_BENEFITS_1_CONSTANTS } from "../fields/programBenefits/programBenefits_1";
import { PROGRAM_BENEFITS_1005_CONSTANTS } from "../fields/programBenefits/programBenefits_1005";
import { PROGRAM_BENEFITS_2_CONSTANTS } from "../fields/programBenefits/programBenefits_2";
import { PROGRAM_BENEFITS_3_CONSTANTS } from "../fields/programBenefits/programBenefits_3";
import { PROGRAM_BENEFITS_4_CONSTANTS } from "../fields/programBenefits/programBenefits_4";
import { PROGRAM_BENEFITS_6_CONSTANTS } from "../fields/programBenefits/programBenefits_6";
import { PROGRAM_BENEFITS_7_CONSTANTS } from "../fields/programBenefits/programBenefits_7";
import { PROGRAM_BENEFITS_12_CONSTANTS } from "../fields/programBenefits/programBenefits_12";
import { RECRUITERS_1_CONSTANTS } from "../fields/recruiter/recruiter_1";
import { SKILLS_1_CONSTANTS } from "../fields/skills/skills_1";
import { SPECIALISATION_1_CONSTANTS } from "../fields/specialisation/specialisation_1";
import { SPECIALISATION_5017_CONSTANTS } from "../fields/specialisation/specialisation_5017";
import { SPECIALISATION_CAROUSEL_1_CONSTANTS } from "../fields/specialisationCarousel/specialisationCarousel_1";
import { SUCCESS_STORIES_1_CONSTANTS } from "../fields/successStories/successStories_1";
import { SUCCESS_STORIES_2_CONSTANTS } from "../fields/successStories/successStories_2";
import { WHAT_MAKES_COURSE_DIFFERENT_1_CONSTANTS } from "../fields/whatMakesCourseDifferent/whatMakesCourseDifferent_1";
import { WHAT_MAKES_COURSE_DIFFERENT_2_CONSTANTS } from "../fields/whatMakesCourseDifferent/whatMakesCourseDifferent_2";
import { WHO_IS_THIS_PROGRAM_FOR_1_CONSTANTS } from "../fields/whoIsThisProgramFor/whoIsThisProgramFor_1";
//import WhatMakesCourseDifferent2 from "@/widgets/whatmakescoursedifferent/WhatMakesCourseDifferent2";
import ApplicationProcess1 from "@/widgets/applicationprocess/ApplicationProcess1";
import ApplicationProcess2 from "@/widgets/applicationprocess/ApplicationProcess2";
import { CourseOutcome1 } from "@/widgets/courseOutcome/CourseOutcome1";
import { CourseOutcome2 } from "@/widgets/courseOutcome/CourseOutcome2";
import { Leaders1 } from "@/widgets/leaders/Leaders1";
import { ProgramBenefits1 } from "@/widgets/programBenefits/ProgramBenefits1";
import ProgramBenefitsCourseFee from "@/widgets/programBenefits/ProgramBenefitsCourseFee";
import ProgramBenefitsFeePlan from "@/widgets/programBenefits/ProgramBenefitsFeePlan";
import ProgramBenefitsGrid3 from "@/widgets/programBenefits/ProgramBenefitsGrid3";
import { Skills1 } from "@/widgets/skills/Skills1";
//import { Leaders1 } from "@/widgets/leaders/Leaders1";
import { createApplicationProcess, updateApplicationProcess } from "@/api/admin/applicationProcess";
import { extractMetadataFields } from "@/helpers";
import {
  activateMetadata,
  getPreviewData,
  isCmsShowFlagOn,
} from "@/utils/variantHelpers";
import ApplicationProcess1030 from "@/widgets/applicationprocess/ApplicationProcess1030";
import { Brand1 } from "@/widgets/brand/Brand1";
import { CaseStudy1 } from "@/widgets/caseStudy/CaseStudy1";
import { Comparison1 } from "@/widgets/comparison/Comparison1";
import { Comparison1004 } from "@/widgets/comparison/Comparison1004";
import Eligibility1041 from "@/widgets/eligibility/Eligibility1041";
import { Institutes1 } from "@/widgets/institutes/Institutes1";
import Leaders1032 from "@/widgets/leaders/Leaders1032";
import ProgramBenefits1005 from "@/widgets/programBenefits/ProgramBenefits1005";
import ProgramBenefits1029 from "@/widgets/programBenefits/ProgramBenefits1029";
import ProgramBenefits1040 from "@/widgets/programBenefits/ProgramBenefits1040";
import ProgramBenefits1044 from "@/widgets/programBenefits/ProgramBenefits1044";
import { ProgramBenefits2 } from "@/widgets/programBenefits/ProgramBenefits2";
import ProgramBenefitsGrid2 from "@/widgets/programBenefits/ProgramBenefitsGrid2";
import ProgramBenefitsPracticalSkills from "@/widgets/programBenefits/ProgramBenefitsPracticalSkills";
import ProgramBenefits5008 from "@/widgets/programBenefits/ProgramBenefits5008";
import ProgramBenefits5009 from "@/widgets/programBenefits/ProgramBenefits5009";
import ProgramBenefits1063 from "@/widgets/programBenefits/ProgramBenefits1063";
import ProgramBenefits1069 from "@/widgets/programBenefits/ProgramBenefits1069";
import Recruiters5010 from "@/widgets/recruiters/Recruiters5010";
import ApplicationProcess5011 from "@/widgets/applicationprocess/ApplicationProcess5011";
import Eligibility5012 from "@/widgets/eligibility/Eligibility5012";
import { APPLICATION_PROCESS_1030_CONSTANTS } from "../fields/applicationProcess/applicationProcess_1030";
import { ELIGIBILITY_1041_CONSTANTS } from "../fields/eligibility/eligibility_1041";
import { LEADERS_1032_CONSTANTS } from "../fields/leaders/leaders_1032";
import { PROGRAM_BENEFITS_1029_CONSTANTS } from "../fields/programBenefits/programBenefits_1029";
import { PROGRAM_BENEFITS_1040_CONSTANTS } from "../fields/programBenefits/programBenefits_1040";
import { PROGRAM_BENEFITS_1044_CONSTANTS } from "../fields/programBenefits/programBenefits_1044";
import { PROGRAM_BENEFITS_5008_CONSTANTS } from "../fields/programBenefits/programBenefits_5008";
import { PROGRAM_BENEFITS_5009_CONSTANTS } from "../fields/programBenefits/programBenefits_5009";
import { PROGRAM_BENEFITS_1063_CONSTANTS } from "../fields/programBenefits/programBenefits_1063";
import { PROGRAM_BENEFITS_1069_CONSTANTS } from "../fields/programBenefits/programBenefits_1069";
import { RECRUITERS_5010_CONSTANTS } from "../fields/recruiter/recruiter_5010";
import { APPLICATION_PROCESS_5011_CONSTANTS } from "../fields/applicationProcess/applicationProcess_5011";
import { ELIGIBILITY_5012_CONSTANTS } from "../fields/eligibility/eligibility_5012";
import {
	APPLICATION_PROCESS_VARIANTS,
	BRAND_VARIANTS,
	CAREER_PROSPECTS_VARIANTS,
	CASE_STUDY_VARIANTS,
	CERTIFICATION_VARIANTS,
	COMPARISON_VARIANTS,
	COURSE_OUTCOME_VARIANTS,
	ELIGIBILITY_VARIANTS,
	INSTITUTES_VARIANTS,
	LEADERS_VARIANTS,
	PROGRAM_BENEFITS_VARIANTS,
	RECRUITERS_VARIANTS,
	SECTION_TYPES,
	SKILLS_VARIANTS,
	SPECIALISATION_AVAILABLE_VARIANTS,
	SPECIALISATION_CAROUSEL_VARIANTS,
	SUCCESS_STORIES_VARIANTS,
	WHAT_MAKES_COURSE_DIFFERENT_VARIANTS,
	WHO_IS_THIS_PROGRAM_FOR_VARIANTS,
} from "../sectionTypes";

export const COURSE_SECTION_VARIANTS = {
	[SECTION_TYPES.RECRUITERS]: [
		{
			id: RECRUITERS_VARIANTS.RECRUITERS_1,
			name: "Recruiters 1",
			description: "Recruiters section",
			componentFields: RECRUITERS_1_CONSTANTS.RECRUITERS_FIELDS_CONFIG,
			...extractMetadataFields(RECRUITERS_1_CONSTANTS),
			showTable: true,
			setAction: setRecruiters1,
			formComponent: Form_1,
			previewComponent: Recruiters1,
			selector: (state: PageState) => state.recruiters_1,
			createMethod: createRecruiters,
			updateMethod: updateRecruiters,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
    {
      id: RECRUITERS_VARIANTS.RECRUITERS_5010,
      name: "Recruiters 5010 — KUK Where Our Graduates Work",
      description:
        "KUK employer logos — a heading, a sub-heading and a logo row (two centred rows below 768px, one wrapping row above it)",
      componentFields: RECRUITERS_5010_CONSTANTS.RECRUITERS_FIELDS_CONFIG,
      ...extractMetadataFields(RECRUITERS_5010_CONSTANTS),
      showTable: true,
      setAction: setRecruiters5010,
      formComponent: Form_1,
      previewComponent: Recruiters5010,
      selector: (state: PageState) => state.recruiters_5010,
      createMethod: createRecruiters,
      updateMethod: updateRecruiters,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
            // Not activateMetadata: the widget drops status-false rows
            // itself, so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
	],
	[SECTION_TYPES.ELIGIBILITY]: [
		{
			id: ELIGIBILITY_VARIANTS.ELIGIBILITY_1,
			name: "Eligibility 1",
			description: "Eligibility criteria section",
			componentFields: ELIGIBILITY_1_CONSTANTS.ELIGIBILITY_FIELDS_CONFIG,
			...extractMetadataFields(ELIGIBILITY_1_CONSTANTS),
			showTable: false,
			setAction: setEligibility1,
			formComponent: Form_1,
			previewComponent: EligibilityCriteria,
			selector: (state: PageState) => state.eligibility_1,
			createMethod: createEligibility,
			updateMethod: updateEligibility,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							status: true,
						},
					},
				};
			},
		},
		{
			id: ELIGIBILITY_VARIANTS.ELIGIBILITY_2,
			name: "Eligibility 2",
			description: "Tabbed eligibility with rich text per category (gradient, CTA)",
			componentFields: ELIGIBILITY_2_CONSTANTS.COMPONENT_FIELDS_CONFIG,
			categoryFieldsConfig: ELIGIBILITY_2_CONSTANTS.CATEGORY_FIELDS_CONFIG,
			itemTableFieldsConfig: ELIGIBILITY_2_CONSTANTS.itemTableFieldsConfig,
			hideCategoryItems: true,
			includeCategoriesInParentSave: true,
			showTable: false,
			setAction: setEligibility2,
			formComponent: Form_3,
			previewComponent: Eligibility2,
			selector: (state: PageState) => state.eligibility_2,
			createMethod: createEligibility,
			updateMethod: updateEligibility,
			getMethod: getEligibilityByComponentId,
			fetchCategoriesMethod: getEligibilityCategoriesByComponentId,
			createCategoryMethod: createEligibilityCategory,
			updateCategoryMethod: updateEligibilityCategory,
			deleteCategoryMethod: deleteEligibilityCategory,
			cloneMethod: cloneEligibilityWithCategories,
			cloneGetSourceId: getEligibilityByComponentId,
			cloneEntityName: "Eligibility",
			cloneNestedEntities: "categories",
			hasParentChildStructure: true,
			childItemsFieldName: "categories",
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				const categories = Array.isArray(data.categories) ? data.categories : [];
				return {
					data: {
						data: {
							...data,
							categories,
							status: true,
						},
					},
				};
			},
		},
		{
			id: ELIGIBILITY_VARIANTS.ELIGIBILITY_1041,
			name: "Eligibility 1041 — BDU Eligibility Criteria",
			description:
				"BDU Eligibility Criteria — heading with a gradient rule over one tinted panel",
			componentFields: ELIGIBILITY_1041_CONSTANTS.ELIGIBILITY_FIELDS_CONFIG,
			showTable: false,
			setAction: setEligibility1041,
			formComponent: Form_1,
			previewComponent: Eligibility1041,
			selector: (state: PageState) => state.eligibility_1041,
			createMethod: createEligibility,
			updateMethod: updateEligibility,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							status: true,
						},
					},
				};
			},
		},
    {
      id: ELIGIBILITY_VARIANTS.ELIGIBILITY_5012,
      name: "Eligibility 5012 — KUK Eligibility Criteria",
      description:
        "KUK eligibility — a heading over a single tinted panel with a leading icon, the criteria and an optional note",
      componentFields: ELIGIBILITY_5012_CONSTANTS.ELIGIBILITY_FIELDS_CONFIG,
      ...extractMetadataFields(ELIGIBILITY_5012_CONSTANTS),
      showTable: true,
      setAction: setEligibility5012,
      formComponent: Form_1,
      previewComponent: Eligibility5012,
      selector: (state: PageState) => state.eligibility_5012,
      createMethod: createEligibility,
      updateMethod: updateEligibility,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
	],
	[SECTION_TYPES.SPECIALISATION_AVAILABLE]: [
		{
			id: SPECIALISATION_AVAILABLE_VARIANTS.SPECIALISATION_AVAILABLE_1,
			name: "Specialisation 1",
			description: "Specialisations available section",
			componentFields: SPECIALISATION_1_CONSTANTS.SPECIALISATION_FIELDS_CONFIG,
			...extractMetadataFields(SPECIALISATION_1_CONSTANTS),
			showTable: true,
			setAction: setSpecialisation1,
			formComponent: Form_1,
			previewComponent: Specialisation1,
			selector: (state: PageState) => state.specialisation_1,
			createMethod: createSpecialisation,
			updateMethod: updateSpecialisation,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: SPECIALISATION_AVAILABLE_VARIANTS.SPECIALISATION_AVAILABLE_5017,
			name: "Specialisation 5017 (KUK)",
			description:
				"KUK programme-specialisation cards: thumbnail, name, blurb, duration and a Know more / Brochure pair.",
			componentFields: SPECIALISATION_5017_CONSTANTS.SPECIALISATION_FIELDS_CONFIG,
			...extractMetadataFields(SPECIALISATION_5017_CONSTANTS),
			showTable: true,
			setAction: setSpecialisation5017,
			formComponent: Form_1,
			previewComponent: Specialisation5017,
			selector: (state: PageState) => state.specialisation_5017,
			createMethod: createSpecialisation,
			updateMethod: updateSpecialisation,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.SPECIALISATION_CAROUSEL]: [
		{
			id: SPECIALISATION_CAROUSEL_VARIANTS.SPECIALISATION_CAROUSEL_1,
			name: "Specialisation Carousel",
			description:
				"Scrollable specialisation cards. Pick image or icon cards with the Card layout dropdown; each card opens a lead form, a URL or a rich-text modal.",
			componentFields:
				SPECIALISATION_CAROUSEL_1_CONSTANTS.SPECIALISATION_CAROUSEL_FIELDS_CONFIG,
			...extractMetadataFields(SPECIALISATION_CAROUSEL_1_CONSTANTS),
			showTable: true,
			setAction: setSpecialisationCarousel3000,
			formComponent: Form_1,
			previewComponent: SpecialisationCarousel3000,
			selector: (state: PageState) => state.specialisation_carousel_3000,
			createMethod: createSpecialisationCarousel,
			updateMethod: updateSpecialisationCarousel,
			dynamicOptionsConfig: {
				courses: {
					fetch: getActiveCourses,
					transform: (course: {
						id?: number;
						program_name?: string;
						brochure?: string;
					}) => ({
						value: String(course.id ?? ""),
						label: course.program_name ?? String(course.id ?? ""),
						brochure: course.brochure ?? "",
					}),
				},
			},
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							design_variant: data.design_variant || "variant_1",
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.CAREER_PROSPECTS]: [
		{
			id: CAREER_PROSPECTS_VARIANTS.CAREER_PROSPECTS_1,
			name: "Career Prospects 1",
			description: "Career prospects section",
			componentFields:
				CAREER_PROSPECTS_1_CONSTANTS.CAREER_PROSPECTS_FIELDS_CONFIG,
			...extractMetadataFields(CAREER_PROSPECTS_1_CONSTANTS),
			showTable: true,
			setAction: setCareerProspects1,
			formComponent: Form_1,
			previewComponent: CareerProspects1,
			selector: (state: PageState) => state.career_prospects_1,
			createMethod: createCareerProspects,
			updateMethod: updateCareerProspects,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: CAREER_PROSPECTS_VARIANTS.CAREER_PROSPECTS_2,
			name: "Career Prospects — Certificate",
			description: "Copy + certificate preview (two columns)",
			componentFields:
				CAREER_PROSPECTS_2_CONSTANTS.CAREER_PROSPECTS_FIELDS_CONFIG,
			...extractMetadataFields(CAREER_PROSPECTS_2_CONSTANTS),
			showTable: false,
			setAction: setCareerProspects2,
			formComponent: Form_1,
			previewComponent: CareerProspects2,
			selector: (state: PageState) => state.career_prospects_2,
			createMethod: createCareerProspects,
			updateMethod: updateCareerProspects,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							status: data.status !== false,
							metadata: [],
						},
					},
				};
			},
		},
		{
			id: CAREER_PROSPECTS_VARIANTS.CAREER_PROSPECTS_3,
			name: "Career Prospects — Image + headline",
			description: "Career Prospects — Image + headline",
			componentFields:
				CAREER_PROSPECTS_3_CONSTANTS.CAREER_PROSPECTS_FIELDS_CONFIG,
			...extractMetadataFields(CAREER_PROSPECTS_3_CONSTANTS),
			showTable: false,
			setAction: setCareerProspects3,
			formComponent: Form_1,
			previewComponent: CareerProspects3,
			selector: (state: PageState) => state.career_prospects_3,
			createMethod: createCareerProspects,
			updateMethod: updateCareerProspects,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							status: data.status !== false,
							metadata: [],
						},
					},
				};
			},
		},
		{
			id: CAREER_PROSPECTS_VARIANTS.CAREER_PROSPECTS_4,
			name: "Career Prospects — Image + headline (background image)",
			description:
				"Image + headline with a desktop background image and a separate mobile image",
			componentFields:
				CAREER_PROSPECTS_4_CONSTANTS.CAREER_PROSPECTS_FIELDS_CONFIG,
			...extractMetadataFields(CAREER_PROSPECTS_4_CONSTANTS),
			showTable: false,
			setAction: setCareerProspects4,
			formComponent: Form_1,
			previewComponent: CareerProspects4,
			selector: (state: PageState) => state.career_prospects_4,
			createMethod: createCareerProspects,
			updateMethod: updateCareerProspects,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							status: data.status !== false,
							metadata: [],
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.CERTIFICATION]: [
		{
			id: CERTIFICATION_VARIANTS.CERTIFICATION_1,
			name: "Certification 1",
			description: "Certification section with carousel",
			componentFields:
				CERTIFICATION_1_CONSTANTS.CERTIFICATION_FIELDS_CONFIG,
			...extractMetadataFields(CERTIFICATION_1_CONSTANTS),
			showTable: true,
			setAction: setCertification1,
			formComponent: Form_1,
			previewComponent: Certificates1,
			selector: (state: PageState) => state.certification_1,
			createMethod: createCertification,
			updateMethod: updateCertification,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.APPLICATION_PROCESS]: [
		{
			id: APPLICATION_PROCESS_VARIANTS.APPLICATION_PROCESS_1,
			name: "Application Process 1",
			description: "Application process with eligibility and admission details",
			componentFields:
				APPLICATION_PROCESS_1_CONSTANTS.APPLICATION_PROCESS_FIELDS_CONFIG,
			...extractMetadataFields(APPLICATION_PROCESS_1_CONSTANTS),
			showTable: true,
			setAction: setApplicationProcess1,
			formComponent: Form_1,
			previewComponent: ApplicationProcess1,
			selector: (state: PageState) => state.application_process_1,
			createMethod: createApplicationProcess,
			updateMethod: updateApplicationProcess,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata_1: activateMetadata(data.metadata_1),
							metadata_2: activateMetadata(data.metadata_2),
							status: true,
						},
					},
				};
			},
		},
		{
			id: APPLICATION_PROCESS_VARIANTS.APPLICATION_PROCESS_2,
			name: "Application Process 2 — admission steps",
			description:
				"Dark layout: headline, primary CTA, numbered admission steps",
			componentFields:
				APPLICATION_PROCESS_2_CONSTANTS.APPLICATION_PROCESS_FIELDS_CONFIG,
			...extractMetadataFields(APPLICATION_PROCESS_2_CONSTANTS),
			showTable: true,
			setAction: setApplicationProcess2,
			formComponent: Form_1,
			previewComponent: ApplicationProcess2,
			selector: (state: PageState) => state.application_process_2,
			createMethod: createApplicationProcess,
			updateMethod: updateApplicationProcess,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata_2: activateMetadata(data.metadata_2),
							status: true,
						},
					},
				};
			},
		},
		{
			id: APPLICATION_PROCESS_VARIANTS.APPLICATION_PROCESS_1030,
			name: "Application Process 1030 — BDU Admission Process",
			description: "BDU Admission Process — numbered step cards under a gradient rule",
			componentFields: APPLICATION_PROCESS_1030_CONSTANTS.APPLICATION_PROCESS_FIELDS_CONFIG,
			...extractMetadataFields(APPLICATION_PROCESS_1030_CONSTANTS),
			showTable: true,
			setAction: setApplicationProcess1030,
			formComponent: Form_1,
			previewComponent: ApplicationProcess1030,
			selector: (state: PageState) => state.application_process_1030,
			createMethod: createApplicationProcess,
			updateMethod: updateApplicationProcess,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata_1: activateMetadata(data.metadata_1),
							status: true,
						},
					},
				};
			},
		},
    {
      id: APPLICATION_PROCESS_VARIANTS.APPLICATION_PROCESS_5011,
      name: "Application Process 5011 — KUK Admission Process",
      description:
        "KUK admission steps — numbered steps beside the university's inline lead form from 1080px, with a full-width CTA standing in below it",
      componentFields: APPLICATION_PROCESS_5011_CONSTANTS.APPLICATION_PROCESS_FIELDS_CONFIG,
      ...extractMetadataFields(APPLICATION_PROCESS_5011_CONSTANTS),
      showTable: true,
      setAction: setApplicationProcess5011,
      formComponent: Form_1,
      previewComponent: ApplicationProcess5011,
      selector: (state: PageState) => state.application_process_5011,
      createMethod: createApplicationProcess,
      updateMethod: updateApplicationProcess,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
            // Not activateMetadata: the widget drops status-false rows
            // itself, so the preview must see the same rows the live page does.
            metadata_1: Array.isArray(data.metadata_1) ? data.metadata_1 : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
	],
	[SECTION_TYPES.SKILLS]: [
		{
			id: SKILLS_VARIANTS.SKILLS_1,
			name: "Skills 1",
			description: "Skills section with certifications and career paths",
			componentFields: SKILLS_1_CONSTANTS.SKILLS_FIELDS_CONFIG,
			...extractMetadataFields(SKILLS_1_CONSTANTS),
			showTable: true,
			setAction: setSkills1,
			formComponent: Form_1,
			previewComponent: Skills1,
			selector: (state: PageState) => state.skills_1,
			createMethod: createSkills,
			updateMethod: updateSkills,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.COMPARISON]: [
		{
			id: COMPARISON_VARIANTS.COMPARISON_1,
			name: "Comparison 1",
			description: "Comparison table: Online MBA vs Traditional vs Standalone",
			componentFields: COMPARISON_1_CONSTANTS.COMPARISON_FIELDS_CONFIG,
			...extractMetadataFields(COMPARISON_1_CONSTANTS),
			showTable: true,
			setAction: setComparison1,
			formComponent: Form_1,
			previewComponent: Comparison1,
			selector: (state: PageState) => state.comparison_1,
			createMethod: createComparison,
			updateMethod: updateComparison,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: COMPARISON_VARIANTS.COMPARISON_1004,
			name: "Comparison 1004",
			description:
				"Comparison 1004 - CU Online vs Traditional Learning: category chips with two comparison columns",
			componentFields: COMPARISON_1004_CONSTANTS.COMPARISON_FIELDS_CONFIG,
			...extractMetadataFields(COMPARISON_1004_CONSTANTS),
			showTable: true,
			setAction: setComparison1004,
			formComponent: Form_1,
			previewComponent: Comparison1004,
			selector: (state: PageState) => state.comparison_1004,
			createMethod: createComparison,
			updateMethod: updateComparison,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.LEADERS]: [
		{
			id: LEADERS_VARIANTS.LEADERS_1,
			name: "Leaders 1",
			description: "Leaders section with description and metadata cards",
			componentFields: LEADERS_1_CONSTANTS.LEADERS_FIELDS_CONFIG,
			...extractMetadataFields(LEADERS_1_CONSTANTS),
			showTable: true,
			setAction: setLeaders1,
			formComponent: Form_1,
			previewComponent: Leaders1,
			selector: (state: PageState) => state.leaders_1,
			createMethod: createLeaders,
			updateMethod: updateLeaders,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: LEADERS_VARIANTS.LEADERS_1032,
			name: "Leaders 1032 — BDU Our Leadership",
			description: "BDU Our Leadership — bordered portrait cards in a scroll rail",
			componentFields: LEADERS_1032_CONSTANTS.LEADERS_FIELDS_CONFIG,
			...extractMetadataFields(LEADERS_1032_CONSTANTS),
			showTable: true,
			setAction: setLeaders1032,
			formComponent: Form_1,
			previewComponent: Leaders1032,
			selector: (state: PageState) => state.leaders_1032,
			createMethod: createLeaders,
			updateMethod: updateLeaders,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.WHO_IS_THIS_PROGRAM_FOR]: [
		{
			id: WHO_IS_THIS_PROGRAM_FOR_VARIANTS.WHO_IS_THIS_PROGRAM_FOR_1,
			name: "Who is this program for 1",
			description: "Who is this program for section with gradient and metadata cards",
			componentFields:
				WHO_IS_THIS_PROGRAM_FOR_1_CONSTANTS.WHO_IS_THIS_PROGRAM_FOR_FIELDS_CONFIG,
			...extractMetadataFields(WHO_IS_THIS_PROGRAM_FOR_1_CONSTANTS),
			showTable: true,
			setAction: setWhoIsThisProgramFor1,
			formComponent: Form_1,
			previewComponent: WhoIsThisProgramFor1,
			selector: (state: PageState) => state.who_is_this_program_for_1,
			createMethod: createWhoIsThisProgramFor,
			updateMethod: updateWhoIsThisProgramFor,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						metadata: activateMetadata(data.metadata),
						status: true,
					},
				};
			},
		},
	],
	[SECTION_TYPES.SUCCESS_STORIES]: [
		{
			id: SUCCESS_STORIES_VARIANTS.SUCCESS_STORIES_1,
			name: "Success Stories 1",
			description: "Success stories section",
			componentFields:
				SUCCESS_STORIES_1_CONSTANTS.SUCCESS_STORIES_FIELDS_CONFIG,
			...extractMetadataFields(SUCCESS_STORIES_1_CONSTANTS),
			showTable: true,
			setAction: setSuccessStories1,
			formComponent: Form_1,
			previewComponent: SuccessStories1,
			selector: (state: PageState) => state.success_stories_1,
			createMethod: createSuccessStories,
			updateMethod: updateSuccessStories,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: SUCCESS_STORIES_VARIANTS.SUCCESS_STORIES_2,
			name: "Student Showcase",
			description: "Heading + subtitle + paged image-card carousel (ASU-009)",
			componentFields:
				SUCCESS_STORIES_2_CONSTANTS.SUCCESS_STORIES_FIELDS_CONFIG,
			...extractMetadataFields(SUCCESS_STORIES_2_CONSTANTS),
			showTable: true,
			setAction: setSuccessStories2,
			formComponent: Form_1,
			previewComponent: StudentShowcase,
			selector: (state: PageState) => state.success_stories_2,
			createMethod: createSuccessStories,
			updateMethod: updateSuccessStories,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.WHAT_MAKES_COURSE_DIFFERENT]: [
		{
			id: WHAT_MAKES_COURSE_DIFFERENT_VARIANTS.WHAT_MAKES_COURSE_DIFFERENT_1,
			name: "What Makes Course Different 1",
			description: "What makes course different section",
			componentFields:
				WHAT_MAKES_COURSE_DIFFERENT_1_CONSTANTS.WHAT_MAKES_COURSE_DIFFERENT_FIELDS_CONFIG,
			...extractMetadataFields(WHAT_MAKES_COURSE_DIFFERENT_1_CONSTANTS),
			showTable: true,
			setAction: setWhatMakesCourseDifferent1,
			formComponent: Form_1,
			previewComponent: WhatMakesCourseDifferent1,
			selector: (state: PageState) => state.what_makes_course_different_1,
			createMethod: createWhatMakesCourseDifferent,
			updateMethod: updateWhatMakesCourseDifferent,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: WHAT_MAKES_COURSE_DIFFERENT_VARIANTS.WHAT_MAKES_COURSE_DIFFERENT_2,
			name: "Solve actual wealth management challenges",
			description: "Solve actual wealth management challenges",
			componentFields:
				WHAT_MAKES_COURSE_DIFFERENT_2_CONSTANTS.WHAT_MAKES_COURSE_DIFFERENT_FIELDS_CONFIG,
			...extractMetadataFields(WHAT_MAKES_COURSE_DIFFERENT_2_CONSTANTS),
			showTable: true,
			setAction: setWhatMakesCourseDifferent2,
			formComponent: Form_1,
			//previewComponent: WhatMakesCourseDifferent2,
			selector: (state: PageState) => state.what_makes_course_different_2,
			createMethod: createWhatMakesCourseDifferent,
			updateMethod: updateWhatMakesCourseDifferent,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.PROGRAM_BENEFITS]: [
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1,
			name: "Program Benefits 1",
			description: "Program benefits with cards and CTA",
			componentFields:
				PROGRAM_BENEFITS_1_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_1_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits1,
			formComponent: Form_1,
			previewComponent: ProgramBenefits1,
			selector: (state: PageState) => state.program_benefits_1,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
		{
			// Variant 2 uses the one-cms "Program Benefits 2" implementation (cards + CTA,
			// program_benefits_2 model), not the standalone degree grid — so it matches how
			// variant-2 data is authored on the multi-tenant backend.
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_2,
			name: "Program Benefits 2",
			description: "Program benefits with cards and CTA",
			componentFields: PROGRAM_BENEFITS_2_CONSTANTS.FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_2_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits2,
			formComponent: Form_1,
			previewComponent: ProgramBenefits2,
			selector: (state: PageState) => state.program_benefits_2,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_3,
			name: "Program Benefits 3 — Curriculum grid (hover)",
			description:
				"Program Benefits 3 — 2×3 curriculum cards; orange hover, icon + text invert",
			componentFields:
				PROGRAM_BENEFITS_3_CONSTANTS.PROGRAM_BENEFITS_3_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_3_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits3,
			formComponent: Form_1,
			previewComponent: ProgramBenefitsGrid3,
			selector: (state: PageState) => state.program_benefits_3,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_4,
			name: "Program Benefits 4 — Course fee",
			description:
				"Headline, CTA, orange eligibility line, total fee + block seat + EMI, prerequisite",
			componentFields:
				PROGRAM_BENEFITS_4_CONSTANTS.PROGRAM_BENEFITS_4_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_4_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits4,
			formComponent: Form_1,
			previewComponent: ProgramBenefitsCourseFee,
			selector: (state: PageState) => state.program_benefits_4,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_5,
			name: "Program Benefits 5",
			description: "Program benefits with cards and CTA",
			componentFields: PROGRAM_BENEFITS_2_CONSTANTS.FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_2_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits2,
			formComponent: Form_1,
			previewComponent: ProgramBenefits2,
			selector: (state: PageState) => state.program_benefits_2,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_6,
			name: "Program Benefits 6 — Fee plan",
			description:
				"Headline, CTA, student-type + scholarship note, total-fee card with optional EMI & markup, per-semester / per-annum boxes, fee notes and eligibility callout",
			componentFields:
				PROGRAM_BENEFITS_6_CONSTANTS.PROGRAM_BENEFITS_6_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_6_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits6,
			formComponent: Form_1,
			previewComponent: ProgramBenefitsFeePlan,
			selector: (state: PageState) => state.program_benefits_6,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							metadata_1: activateMetadata(data.metadata_1),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_7,
			name: "Program Benefits 7 — Practical skills",
			description:
				"Centered headline + description, a left column of icon + title + description rows, and a right featured image (separate mobile asset)",
			componentFields:
				PROGRAM_BENEFITS_7_CONSTANTS.PROGRAM_BENEFITS_7_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_7_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits7,
			formComponent: Form_1,
			previewComponent: ProgramBenefitsPracticalSkills,
			selector: (state: PageState) => state.program_benefits_7,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
		{
			/**
			 * Degree grid — the Figma "Degree that moves with you" 3x2 icon-card grid.
			 *
			 * Held id "2" until unified-tenancy repointed that id at the Program Benefits 2
			 * carousel. The two read DISJOINT column sets off the same `program_benefits`
			 * row (grid: `subtitle` / `section_background_image` / `meta_icon_bg_color`;
			 * carousel: `description` / `left_navigation_icon` / `enable_stack_carousel`),
			 * so sharing an id fed each one the other's blanks — which is exactly the
			 * all-null Zod failure this moved away from. Everything below is prod's
			 * variant-2 entry verbatim; only `id` and `name` differ.
			 */
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_3000,
			name: "Program Benefits 3000 — Degree grid",
			description: "Program Benefits — Degree grid",
			componentFields:
				PROGRAM_BENEFITS_12_CONSTANTS.PROGRAM_BENEFITS_12_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_12_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits12,
			formComponent: Form_1,
			previewComponent: ProgramBenefitsGrid2,
			selector: (state: PageState) => state.program_benefits_12,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1005,
			name: "Program Benefits 1005 — CU flip cards",
			description:
				"Program Benefits 1005 — Centered headline + description with optional lead CTA, and a wrapping grid of benefit cards (icon + title) that recolor on desktop hover and flip on mobile tap to reveal the description",
			componentFields:
				PROGRAM_BENEFITS_1005_CONSTANTS.PROGRAM_BENEFITS_1005_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_1005_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits1005,
			formComponent: Form_1,
			previewComponent: ProgramBenefits1005,
			selector: (state: PageState) => state.program_benefits_1005,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1029,
			name: "Program Benefits 1029 — BDU Key Benefits",
			description: "BDU Key Benefits — tinted background art and icon cards",
			componentFields: PROGRAM_BENEFITS_1029_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_1029_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits1029,
			formComponent: Form_1,
			previewComponent: ProgramBenefits1029,
			selector: (state: PageState) => state.program_benefits_1029,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1040,
			name: "Program Benefits 1040 — BDU Programme Roadmap",
			description:
				"BDU Key Program Features — background art with three-up icon / title / description columns",
			componentFields: PROGRAM_BENEFITS_1040_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_1040_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits1040,
			formComponent: Form_1,
			previewComponent: ProgramBenefits1040,
			selector: (state: PageState) => state.program_benefits_1040,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
		{
			id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1044,
			name: "Program Benefits 1044 — BDU Learn More",
			description:
				"BDU Learn More — heading, gradient rule and rich-text cards with a Read more clamp",
			componentFields: PROGRAM_BENEFITS_1044_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
			...extractMetadataFields(PROGRAM_BENEFITS_1044_CONSTANTS),
			showTable: true,
			setAction: setProgramBenefits1044,
			formComponent: Form_1,
			previewComponent: ProgramBenefits1044,
			selector: (state: PageState) => state.program_benefits_1044,
			createMethod: createProgramBenefits,
			updateMethod: updateProgramBenefits,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
    {
      id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_5008,
      name: "Program Benefits 5008 — KUK Why KUK Online",
      description:
        "KUK benefit cards — a washed-out photograph behind an icon, a title and a line of copy, with the wash alternating by card position",
      componentFields: PROGRAM_BENEFITS_5008_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_BENEFITS_5008_CONSTANTS),
      showTable: true,
      setAction: setProgramBenefits5008,
      formComponent: Form_1,
      previewComponent: ProgramBenefits5008,
      selector: (state: PageState) => state.program_benefits_5008,
      createMethod: createProgramBenefits,
      updateMethod: updateProgramBenefits,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
            // Not activateMetadata: the widget drops status-false rows
            // itself, so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
    {
      id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_5009,
      name: "Program Benefits 5009 — KUK Career Roadmap",
      description:
        "KUK career-path cards — a two-column grid from 1080px, a horizontal rail below it, and a counsellor CTA at both widths",
      componentFields: PROGRAM_BENEFITS_5009_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_BENEFITS_5009_CONSTANTS),
      showTable: true,
      setAction: setProgramBenefits5009,
      formComponent: Form_1,
      previewComponent: ProgramBenefits5009,
      selector: (state: PageState) => state.program_benefits_5009,
      createMethod: createProgramBenefits,
      updateMethod: updateProgramBenefits,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
            // Not activateMetadata: the widget drops status-false rows
            // itself, so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
    {
      id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1063,
      name: "Program Benefits 1063 — KUK Why Choose Us",
      description:
        "KUK Why-Choose-Us cards — a horizontal rail with measured arrows from 768px, a two-column grid below it, and a lead-form CTA at both widths",
      componentFields: PROGRAM_BENEFITS_1063_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_BENEFITS_1063_CONSTANTS),
      showTable: true,
      setAction: setProgramBenefits1063,
      formComponent: Form_1,
      previewComponent: ProgramBenefits1063,
      selector: (state: PageState) => state.program_benefits_1063,
      createMethod: createProgramBenefits,
      updateMethod: updateProgramBenefits,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
    {
      id: PROGRAM_BENEFITS_VARIANTS.PROGRAM_BENEFITS_1069,
      name: "Program Benefits 1069 — KUK Learn More",
      description:
        "KUK Learn More — a tinted band of rich-text cards, each clamped with a View more / View less toggle",
      componentFields: PROGRAM_BENEFITS_1069_CONSTANTS.PROGRAM_BENEFITS_FIELDS_CONFIG,
      ...extractMetadataFields(PROGRAM_BENEFITS_1069_CONSTANTS),
      showTable: true,
      setAction: setProgramBenefits1069,
      formComponent: Form_1,
      previewComponent: ProgramBenefits1069,
      selector: (state: PageState) => state.program_benefits_1069,
      createMethod: createProgramBenefits,
      updateMethod: updateProgramBenefits,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself,
              // so the preview must see the same rows the live page does.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: isCmsShowFlagOn(data.status, true),
            },
          },
        };
      },
    },
	],
	[SECTION_TYPES.COURSE_OUTCOME]: [
		{
			id: COURSE_OUTCOME_VARIANTS.COURSE_OUTCOME_1,
			name: "Course Outcome 1",
			description: "Where this course will take you",
			componentFields:
				COURSE_OUTCOME_1_CONSTANTS.COURSE_OUTCOME_FIELDS_CONFIG,
			...extractMetadataFields(COURSE_OUTCOME_1_CONSTANTS),
			showTable: true,
			setAction: setCourseOutcome1,
			formComponent: Form_1,
			previewComponent: CourseOutcome1,
			selector: (state: PageState) => state.course_outcome_1,
			createMethod: createCourseOutcome,
			updateMethod: updateCourseOutcome,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							font_family_variant: data.font_family_variant || "variant_2",
							status: true,
						},
					},
				};
			},
		},
		{
			id: COURSE_OUTCOME_VARIANTS.COURSE_OUTCOME_2,
			name: "Course Outcome 2",
			description:
				"Where this program can take you – 3×3 image card grid (max 9) with optional title, paragraph & highlight pill",
			componentFields:
				COURSE_OUTCOME_2_CONSTANTS.COURSE_OUTCOME_FIELDS_CONFIG,
			...extractMetadataFields(COURSE_OUTCOME_2_CONSTANTS),
			showTable: true,
			setAction: setCourseOutcome1,
			formComponent: Form_1,
			previewComponent: CourseOutcome2,
			selector: (state: PageState) => state.course_outcome_1,
			createMethod: createCourseOutcome,
			updateMethod: updateCourseOutcome,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.CASE_STUDY]: [
		{
			id: CASE_STUDY_VARIANTS.CASE_STUDY_1,
			name: "Case Study 1",
			description: "Solve actual wealth — case study cards with slider and CTA",
			componentFields: CASE_STUDY_1_CONSTANTS.CASE_STUDY_FIELDS_CONFIG,
			...extractMetadataFields(CASE_STUDY_1_CONSTANTS),
			showTable: true,
			setAction: setCaseStudy1,
			formComponent: Form_1,
			previewComponent: CaseStudy1,
			selector: (state: PageState) => state.case_study_1,
			createMethod: createCaseStudy,
			updateMethod: updateCaseStudy,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.BRAND]: [
		{
			id: BRAND_VARIANTS.BRAND_1,
			name: "Brand 1",
			description: "Brand logos pill grid — 'for leading brands'",
			componentFields: BRAND_1_CONSTANTS.BRAND_FIELDS_CONFIG,
			...extractMetadataFields(BRAND_1_CONSTANTS),
			showTable: true,
			setAction: setBrand1,
			formComponent: Form_1,
			previewComponent: Brand1,
			selector: (state: PageState) => state.brand_1,
			createMethod: createBrand,
			updateMethod: updateBrand,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
	],
	[SECTION_TYPES.INSTITUTES]: [
		{
			id: INSTITUTES_VARIANTS.INSTITUTES_1,
			name: "Institutes 1",
			description: "Heading + text chips — 'leading financial institutions'",
			componentFields: INSTITUTES_1_CONSTANTS.INSTITUTES_FIELDS_CONFIG,
			...extractMetadataFields(INSTITUTES_1_CONSTANTS),
			showTable: true,
			setAction: setInstitutes1,
			formComponent: Form_1,
			previewComponent: Institutes1,
			selector: (state: PageState) => state.institutes_1,
			createMethod: createInstitutes,
			updateMethod: updateInstitutes,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							...data,
							metadata: activateMetadata(data.metadata),
							enable_animation: Boolean(
								data.enable_animation === true || data.enable_animation === 1,
							),
							status: true,
						},
					},
				};
			},
		},
	],
};
